/**
 * Purpose: List businesses belonging to a society with compact mobile toolbar.
 * Inputs:  society (object), onError (fn<string>).
 * Outputs: Rendered businesses grid + pagination.
 * Behavior: Loads businesses via businessService filtered by society.id with
 *           paginated results and client-selected filters/sort.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import BusinessGrid from '../../businesses/BusinessGrid';
import Pagination from '../../common/Pagination';
import { businessService } from '../../../services/businessService';
import { getBusinessUrl } from '../../../utils/urlHelpers';
import SocietySectionToolbar from './SocietySectionToolbar';
import SectionFilterSelect from './SectionFilterSelect';

const CATEGORIES = [
  { value: 'shop', label: 'Shop' },
  { value: 'company', label: 'Company' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'cafe', label: 'Cafe' },
  { value: 'salon', label: 'Salon' },
  { value: 'clinic', label: 'Clinic' },
  { value: 'school', label: 'School' },
  { value: 'gym', label: 'Gym' },
  { value: 'pharmacy', label: 'Pharmacy' },
  { value: 'bank', label: 'Bank' },
  { value: 'property_dealer', label: 'Property Dealer' },
  { value: 'other', label: 'Other' },
];

const SORT_TO_ORDERING = {
  newest: '-created_at',
  oldest: 'created_at',
  highest_rated: '-rating',
  most_reviewed: '-total_ratings',
  name: 'name',
};

const SocietyBusinesses = ({ society, onError }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    sort: 'newest',
  });

  useEffect(() => {
    const loadBusinesses = async () => {
      setLoading(true);
      try {
        const params = {
          page: currentPage,
          search: filters.search,
          category: filters.category !== 'all' ? filters.category : undefined,
          ordering: SORT_TO_ORDERING[filters.sort] || '-created_at',
          society: society.id,
          is_public: true,
        };
        const response = await businessService.getBusinesses(params);
        const businessesData = response.results || response;
        setBusinesses(businessesData);
        setTotalPages(response.total_pages || 1);
        setTotalCount(response.count || businessesData.length);
      } catch (error) {
        console.error('Error loading businesses:', error);
        onError('Failed to load businesses');
      } finally {
        setLoading(false);
      }
    };

    if (society?.id) {
      loadBusinesses();
    }
  }, [society?.id, currentPage, filters, onError]);

  const updateFilters = (partial) => {
    setFilters((prev) => ({ ...prev, ...partial }));
    setCurrentPage(1);
  };

  const activeFilterCount =
    (filters.category !== 'all' ? 1 : 0) + (filters.sort !== 'newest' ? 1 : 0);

  return (
    <div className="space-y-4 sm:space-y-6">
      <SocietySectionToolbar
        title="Businesses"
        count={totalCount}
        itemLabel={{ singular: 'business', plural: 'businesses' }}
        societyName={society.name}
        searchPlaceholder="Search businesses..."
        searchValue={filters.search}
        onSearchChange={(v) => updateFilters({ search: v })}
        activeFilterCount={activeFilterCount}
        addButton={
          isAuthenticated
            ? {
                label: 'Add Business',
                onClick: () =>
                  navigate(`/societies/${society.slug}/businesses/create?society=${society.id}`),
              }
            : null
        }
      >
        <SectionFilterSelect
          label="Category"
          value={filters.category}
          onChange={(e) => updateFilters({ category: e.target.value })}
        >
          <option value="all">All Categories</option>
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </SectionFilterSelect>
        <SectionFilterSelect
          label="Sort By"
          value={filters.sort}
          onChange={(e) => updateFilters({ sort: e.target.value })}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="highest_rated">Highest Rated</option>
          <option value="most_reviewed">Most Reviewed</option>
          <option value="name">Name A-Z</option>
        </SectionFilterSelect>
      </SocietySectionToolbar>

      <BusinessGrid
        businesses={businesses}
        loading={loading}
        societySlug={society.slug}
        onBusinessClick={(business) => navigate(getBusinessUrl(business, society.slug))}
      />

      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default SocietyBusinesses;
